import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Float32BufferAttribute, Group, type Line } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { CircleEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { ArcUtils } from '@/plugins/cad/module/utils/ArcUtils'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'

/**
 * 圆绘制模式。
 */
export enum CircleDrawMode {
  /** 三点法 */
  ThreePoints = 1,
  /** 两点直径法 */
  TwoPoints,
  /** 圆心加半径法 */
  CenterRadius,
}

interface ICenterParams {
  center: IPoint
  radius: number
  segmentsNum?: number
}

const circleMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
  },
  guards: {
    isMode: () => false,
  },
}).createMachine({
  id: 'drawCircleTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [
          { target: 'draw.twoPoints', guard: { type: 'isMode', params: CircleDrawMode.TwoPoints } },
          {
            target: 'draw.centerRadius',
            guard: { type: 'isMode', params: CircleDrawMode.CenterRadius },
          },
          { target: 'draw.threePoints' },
        ],
      },
    },
    draw: {
      initial: 'threePoints',
      states: {
        // 模式一：三点圆
        threePoints: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.firstPoint },
            },
            point2: {
              on: { NEXT: 'point3' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.secondPoint },
            },
            point3: {
              on: { NEXT: '#drawCircleTool.finished', PREVIOUS: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.thirdPoint },
            },
          },
        },
        // 模式二：两点直径
        twoPoints: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.firstDiameterPoint },
            },
            point2: {
              on: { NEXT: '#drawCircleTool.finished' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.secondDiameterPoint },
            },
          },
        },
        // 模式三：圆心+半径
        centerRadius: {
          initial: 'center',
          states: {
            center: {
              on: { NEXT: 'radius' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.centerPoint },
            },
            radius: {
              on: { NEXT: '#drawCircleTool.finished' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.circle.radius },
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 圆绘制工具，支持三点、两点直径与圆心加半径三种模式。
 */
@injectable()
export class DrawCircleTool extends StateMachineTool<typeof circleMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawCircleTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = circleMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
    guards: {
      isMode: (_v, mode) => {
        return this.mode === mode
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private mode: CircleDrawMode = CircleDrawMode.ThreePoints
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private circleObject: Line2 | null = null
  private dimensionGroup: LineDimensionGroup | null = null
  private previewLineObject: Line | null = null
  private entity: CircleEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private circleParams: ICenterParams | null = null

  protected onInitialize(
    entityData?: Partial<CircleEntity>,
    mode: CircleDrawMode = CircleDrawMode.ThreePoints
  ): void {
    this.mode = mode
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.circleObject && DrawUtils.disposeObject(this.circleObject)
    this.dimensionGroup?.dispose()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.circleObject = null
    this.dimensionGroup = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
    this.circleParams = null
  }

  /**
   * 鼠标按下：推进状态机并记录一个点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新圆预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新直径/半径标注 */
  onViewerUpdate() {
    this.dimensionGroup?.update()
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }
    const p1 = points[points.length - 2]
    const p2 = points[points.length - 1]
    // 绘制辅助预览线
    this.updatePreviewLine(p1, p2)

    // 绘制标注
    this.updateDimensionGroup(p1, p2)

    // 计算圆弧参数
    const circleParams = this.getCircleParamsByMode(this.mode, points)
    this.updateCircleObject(circleParams)
    this.circleParams = circleParams
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(20)
      const materialOption = {
        color: Setting.previewLineConfig.color,
        transparent: Setting.previewLineConfig.opacity < 1,
        opacity: Setting.previewLineConfig.opacity,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedLine(p1, p2, materialOption, this.group)
      return
    }

    const positions = [p1, p2].flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  private updateDimensionGroup(startPoint: IPoint, endPoint: IPoint) {
    if (this.dimensionGroup) {
      this.dimensionGroup.updateLine(startPoint, endPoint)
      return
    }
    this.dimensionGroup = new LineDimensionGroup(
      startPoint,
      endPoint,
      {},
      this.group,
      this.controller.domElement!.parentElement!,
      (point: IPoint) => this.controller.getScreenPosition(point),
      (radius: number) => this.controller.getPixelSizeInWorld(radius),
      this.unitPresentation
    )
  }

  private updateCircleObject(params: ICenterParams | null) {
    if (!params || !this.entity) {
      this.circleObject && (this.circleObject.visible = false)
      return
    }
    const segmentsNum = ArcUtils.getDynamicSegments(
      params.radius,
      this.controller.getPixelSizeInWorld(1)
    )
    params.segmentsNum = segmentsNum
    const options = {
      center: { x: 0, y: 0, z: 0 },
      radius: params.radius,
      segmentsNum,
    }
    const points = DrawUtils.getArcPoints(options)
    const geometry = DrawUtils.getLineGeometryByPoints(points)
    if (!this.circleObject) {
      const material = MaterialUtils.createMaterialByLineType(
        this.entity.lineType,
        MaterialUtils.getMaterialOptionByEntity(this.entity)
      )
      this.circleObject = createCadLine2Object(geometry, material)
      this.circleObject.position.set(params.center.x, params.center.y, 0)
      this.circleObject.computeLineDistances()
      this.group.add(this.circleObject)
      return
    }
    this.circleObject.geometry.dispose()
    this.circleObject.geometry = geometry
    this.circleObject.position.set(params.center.x, params.center.y, 0)
    this.circleObject.computeLineDistances()
    this.circleObject.visible = true
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<CircleEntity>) {
    const entity = new CircleEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.circleParams) {
      Object.assign(this.entity, { ...this.circleParams })
    }
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    return this.entity ? this.entity.radius > 1e-2 : false
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }

  private getCircleParamsByMode(mode: CircleDrawMode, points: IPoint[]): ICenterParams | null {
    if (mode === CircleDrawMode.ThreePoints && points.length >= 3) {
      const [p1, p2, p3] = points.slice(-3)
      return this.getCircleParamsByThreePoints(p1, p2, p3)
    } else if (mode === CircleDrawMode.TwoPoints && points.length >= 2) {
      const [p1, p2] = points.slice(-2)
      return this.getCircleParamsByTwoPoints(p1, p2)
    } else if (mode === CircleDrawMode.CenterRadius && points.length >= 2) {
      const [center, radiusPoint] = points.slice(-2)
      return this.getCircleParamsByCenterRadius(center, radiusPoint)
    }
    return null
  }

  private getCircleParamsByThreePoints(p1: IPoint, p2: IPoint, p3: IPoint): ICenterParams | null {
    // 根据三点计算圆心和半径
    const { x: x1, y: y1 } = p1
    const { x: x2, y: y2 } = p2
    const { x: x3, y: y3 } = p3

    // 1. 计算分母 (Determinant)
    // 如果 D 为 0，说明三点共线
    const D = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2))

    if (Math.abs(D) < 1e-6) {
      return null // 三点共线，无法构成圆
    }

    // 2. 利用公式计算圆心坐标 (x, y)
    const x1_2 = x1 * x1
    const y1_2 = y1 * y1
    const x2_2 = x2 * x2
    const y2_2 = y2 * y2
    const x3_2 = x3 * x3
    const y3_2 = y3 * y3

    const centerX =
      ((x1_2 + y1_2) * (y2 - y3) + (x2_2 + y2_2) * (y3 - y1) + (x3_2 + y3_2) * (y1 - y2)) / D

    const centerY =
      ((x1_2 + y1_2) * (x3 - x2) + (x2_2 + y2_2) * (x1 - x3) + (x3_2 + y3_2) * (x2 - x1)) / D

    const center = { x: centerX, y: centerY }

    // 3. 计算半径 (圆心到任意一点的距离)
    const radius = Math.sqrt((centerX - x1) ** 2 + (centerY - y1) ** 2)

    return { center, radius }
  }

  private getCircleParamsByTwoPoints(p1: IPoint, p2: IPoint): ICenterParams {
    const centerX = (p1.x + p2.x) / 2
    const centerY = (p1.y + p2.y) / 2
    const radius = Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2) / 2
    return {
      center: { x: centerX, y: centerY },
      radius,
    }
  }

  private getCircleParamsByCenterRadius(center: IPoint, radiusPoint: IPoint): ICenterParams {
    const radius = Math.sqrt((center.x - radiusPoint.x) ** 2 + (center.y - radiusPoint.y) ** 2)
    return {
      center: { x: center.x, y: center.y },
      radius,
    }
  }
}
